import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { DataTablesModule } from 'angular-datatables';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { selectUsersWithFundingSource } from 'app/manage-auctions/features/auction.selectors';
import { ModalAuctionDetailService } from 'app/manage-auctions/services/modal-auction-detail/modal-auction-detail.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-auction-bidders-tab',
  standalone: true,
  imports: [CommonModule,DataTablesModule],
  templateUrl: './auction-bidders-tab.component.html',
  styleUrl: './auction-bidders-tab.component.scss'
})
export class AuctionBiddersTabComponent implements OnInit, AfterViewInit {

  @Input() public auction;

  public notDeletedOptions: any[];
  public aggregatedBids: any[];
  private usersWithFundingSourceMap = null;
  public dtOptions = {};
  public dtTrigger: Subject<ADTSettings> = new Subject<ADTSettings>();
  @ViewChild('biddersDatatable') datatableElement!: ElementRef;

  DELETED_OPTION_STATUS = 1;

  constructor(
    private store: Store,
    private modalAuctionDetailService: ModalAuctionDetailService
  ) { }

  private usersWithFundingSource$ = this.store.pipe(
    select(selectUsersWithFundingSource),
    takeUntilDestroyed(),
  )

  ngOnInit(): void {
    this.usersWithFundingSource$.subscribe(map => {
      this.usersWithFundingSourceMap = map;
    })

    this.notDeletedOptions = this.auction.campaignTypeData.presetOptions
      .filter(auctionItem => auctionItem.status !== this.DELETED_OPTION_STATUS)
    this.aggregatedBids = this.aggregateUserData(this.notDeletedOptions);
  }

  ngAfterViewInit(): void {
    this.renderDatatable();
  }

  aggregateUserData(options: any[]) {
    const userMap = new Map<string, {id:string; name: string;email: string; address: string; phone: string; itemsBiddedOn:Set<string>; itemsBiddedOnCount: number; itemsSoldCount }>();
    
    options.forEach(item => {
      item.soldItems?.forEach(soldItem => {
        const user = soldItem.userInfo;
        let address:string;
        if(user.address && user.address.street1 != ''){
          address = window['utils'].getStreetAddressText(user.address, true)
        }

        if(!userMap.has(user.id)) {
          userMap.set(user.id, {
            id: user.id,
            name: user.name,
            email: user.email || "NA",
            phone: user.phone || "NA",
            address: address || "NA",
            itemsBiddedOn: new Set(),
            itemsBiddedOnCount: 0,
            itemsSoldCount: 1,
          });
        } else{
          userMap.get(user.id).itemsSoldCount++;
        }
      });
      item.currentBids?.forEach((bid: any) => {
        const user = bid.userInfo;
        if (!user) return;
  
        let address:string;
        if(user.address && user.address.street1 != ''){
          address = window['utils'].getStreetAddressText(user.address, true)
        }
        
        if(!userMap.has(user.id)) {
          userMap.set(user.id, {
            id: user.id,
            name: user.name,
            email: user.email || "NA",
            phone: user.phone || "NA",
            address: address || "NA",
            itemsBiddedOn: new Set(),
            itemsBiddedOnCount: 0,
            itemsSoldCount: 0,
          });
        }

        const userData = userMap.get(user.id);
        userData.itemsBiddedOn.add(item.id);
        userData.itemsBiddedOnCount = userData.itemsBiddedOn.size;
      });
    });
  
    return Array.from(userMap.values());
  }

  renderDatatable(){
    const self = this;
    this.dtOptions = {
    data: this.aggregatedBids,
    autoWidth: true,
    pageLength: 10,
    paging: true,
    fixedColumns: {
      left:1,
    },
    deferRender: true,
    destroy: true,
    dom: "<'table__header--paybee d-flex align-items-center justify-content-between pb-3 pt-3'<''B><''f>><'table-responsive't><'table__footer--paybee d-flex align-items-center justify-content-between p-4'ip>",
    buttons: [{
        className: 'btn-copy-code btn btn-outline-grey btn-sm px-2 waves-effect',
        extend: 'excel',
        filename: 'Auction_Bidders',
        text: "<i class='fas fa-cloud-download-alt mr-1'></i> Export",
        title: null,
        exportOptions: {
            stripHtml: true,
            columns: "thead th:not(.no-export)"
        }
    }],
    columns: [
        {
          data: "name",
          render(name, type, row) {
            if(type === 'display'){
                const fundingSource = self.usersWithFundingSourceMap.get(row.id)?.fundingSource;
                if (fundingSource) {
                  return `
                      <div class="d-flex align-items-center">
                          <span
                            data-toggle="fullNameTooltip"
                            data-placement="top"
                            data-original-title="${name}"
                            class="table-name-container blue-text mr-2 cursor--pointer w-auto"
                          >${name}</span>
                          <svg
                              focusable="false"
                              viewBox="0 0 32 21"
                              data-toggle="cardInfoTooltip"
                              style="width: 20px; cursor: pointer; flex-shrink:0"
                              placement="left"
                              title="${fundingSource.sourceName}"
                          >
                              <g fill="green" fill-rule="evenodd">
                              <g class="Icon-fill">
                                  <g transform="translate(0 2)">
                                  <path d="M26.58 19H2.42A2.4 2.4 0 0 1 0 16.62V2.38A2.4 2.4 0 0 1 2.42 0h24.16A2.4 2.4 0 0 1 29 2.38v14.25A2.4 2.4 0 0 1 26.58 19zM10 5.83c0-.46-.35-.83-.78-.83H3.78c-.43 0-.78.37-.78.83v3.34c0 .46.35.83.78.83h5.44c.43 0 .78-.37.78-.83V5.83z" opacity=".2"></path>
                                  <path d="M25 15h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0H4c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1z" opacity=".3"></path>
                                  </g>
                              </g>
                              </g>
                          </svg>
                      </div>
                  `;
              }

              return `<span 
                  data-toggle="fullNameTooltip"
                  data-placement="top"
                  data-original-title="${name}"
                  class="table-name-container blue-text cursor--pointer">${name}</span>`
            } 

            return name;
          },
          createdCell: function(cell, cellData, rowData, row, col){
            const callback = () => {  
              self.openAuctionDetailModal(rowData.id);
            }
            cell?.removeEventListener('click', callback);
            cell?.addEventListener('click', callback);
          }
        },
        {
          data: "email",
        },
        {
          data: "phone",
        },
        {
          data: "address",
        },
        {
          data: "itemsBiddedOnCount",
        },
        {
          data: "itemsSoldCount",
        },
      ],
      drawCallback: function(){
        window['$']('[data-toggle="cardInfoTooltip"]').tooltip()

        const datatableEl = self.datatableElement.nativeElement as HTMLElement;
        const scrollContainer = datatableEl.parentElement;
    
        if (scrollContainer) {
          window['dataTableService'].registerScrollEvent(window['$'](scrollContainer));
        }

        setTimeout(() => {
          window['dataTableService'].scrollCallback(window['$'](scrollContainer))
          
          window['$']('[data-toggle="fullNameTooltip"]')
           .filter(function () {
              const isSelf = this.classList.contains('table-name-container');
              const container = isSelf ? this : this.querySelector('.table-name-container');
              return container && container.scrollWidth > container.offsetWidth;
            })
            .tooltip();
        })
      } 
    }

    setTimeout(() => {
      // race condition fails unit tests if dtOptions isn't sent with dtTrigger
      if (!this.dtTrigger.closed) {
        this.dtTrigger.next(this.dtOptions);
      }
    }, 200);
  }

  openAuctionDetailModal(userId) {
    const data = {
      userId,
      placeOfCall: 'bidderDetails'
    }

    this.modalAuctionDetailService.open(data);
  }
}